import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { nativeSubmit } from "@/lib/native-submit"

export type SsoProviders = {
  google: boolean
  microsoft: boolean
  openidConnect: boolean
  openidConnectProvider: string | null
}

type SsoProvider = "openidConnect" | "google" | "microsoft"

type SsoButtonsProps = {
  ssoProviders: SsoProviders
  termsAccepted: boolean
  disabled?: boolean
  onLoadingChange?: (loading: boolean) => void
}

const PROVIDER_PATHS: Record<SsoProvider, string> = {
  openidConnect: "/users/auth/openid_connect",
  google: "/users/auth/google",
  microsoft: "/users/auth/microsoft"
}

export const SsoButtons = ({ ssoProviders, termsAccepted, disabled, onLoadingChange }: SsoButtonsProps) => {
  const { t } = useTranslation()
  const [loadingProvider, setLoadingProvider] = useState<SsoProvider | null>(null)

  const start = (provider: SsoProvider) => {
    if (loadingProvider) return
    setLoadingProvider(provider)
    onLoadingChange?.(true)
    try {
      nativeSubmit("post", PROVIDER_PATHS[provider])
    } catch {
      toast.error(t("landing.login.errors.sso_failed"))
      setLoadingProvider(null)
      onLoadingChange?.(false)
    }
  }

  const isDisabled = !termsAccepted || disabled || loadingProvider !== null

  const renderButton = (provider: SsoProvider, label: string) => (
    <Button type="button" className="w-full" onClick={() => start(provider)} disabled={isDisabled}>
      {loadingProvider === provider && (
        <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
      )}
      {loadingProvider === provider ? t("landing.login.redirecting") : label}
    </Button>
  )

  return (
    <>
      {ssoProviders.openidConnect &&
        renderButton(
          "openidConnect",
          t("landing.login.continue_with_provider", { provider: ssoProviders.openidConnectProvider })
        )}
      {ssoProviders.google && renderButton("google", t("landing.login.continue_with_google"))}
      {ssoProviders.microsoft && renderButton("microsoft", t("landing.login.continue_with_microsoft"))}
    </>
  )
}
